/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';
import isNil from 'lodash.isnil';
import {GenericObject} from '@ion/core/src/types/entities';
import {isDeepEqual} from '@ion/core/src/utils/Equality';
import {IonGridAPI} from '@ion/dls/src/grid/IonGrid';
import useBulkOperationResults from './useBulkOperationResults';
import useCRUDOperationResults from './useCRUDOperationResults';

/**
 * The generic hook will determine the grid update based on the operation. This will
 * reload the current block if the new entity creation/deletion is successful. Else just
 * update the updated row without reloading.
 *
 * ```typescript
 *
 *   import {useGridDataUpdate} from '@ion/dls/src/hooks/';
 *
 *   useGridDataUpdate('adminAssets', ionGridRef.current);
 *
 * ```
 */
export const useGridDataUpdate = (reducer: string, gridAPI: IonGridAPI, action?: Function): void => {
  const dispatch        = useDispatch();
  const previousResults = useRef({});
  const {
    addResult,
    editResult,
    updateResult,
    deleteResult
  } = useCRUDOperationResults(reducer);
  const {
    bulkEditResult,
    bulkDeleteResult,
    bulkImportResult,
    bulkMoveResult
  } = useBulkOperationResults(reducer);

  const refreshRequired = (current, key) => {
    return current && !isDeepEqual(current, previousResults.current?.[key]);
  };

  const updateState = (newState: GenericObject) => {
    previousResults.current = {
      ...previousResults?.current,
      ...newState
    };
  };

  const postUpdate = useCallback(() => {
    action && dispatch(action());
  }, [action]);

  useEffect(() => {
    if (
      refreshRequired(bulkDeleteResult, 'bulkDeleteResult') ||
      refreshRequired(addResult, 'addResult') ||
      refreshRequired(deleteResult, 'deleteResult')
    ) {
      gridAPI?.refreshCurrentBlock?.();
      postUpdate();
    }
    updateState({
      bulkDeleteResult,
      addResult,
      deleteResult
    });
  }, [addResult, deleteResult, bulkDeleteResult]);

  useEffect(() => {
    if (refreshRequired(bulkMoveResult, 'bulkMoveResult')) {
      gridAPI?.refreshCurrentBlock?.();
    }
    updateState({bulkMoveResult});
  }, [bulkMoveResult]);

  useEffect(() => {
    if (
      refreshRequired(editResult, 'editResult') ||
      refreshRequired(updateResult, 'updateResult')
    ) {
      gridAPI?.updateRowNodes?.([editResult || updateResult]);
      postUpdate();
    }
    updateState({editResult, updateResult});
  }, [editResult, updateResult]);

  useEffect(() => {
    if (refreshRequired(bulkEditResult, 'bulkEditResult')) {
      gridAPI?.updateRowNodes?.(bulkEditResult as GenericObject[]);
      postUpdate();
    }
    updateState({bulkEditResult});
  }, [bulkEditResult]);

  useEffect(() => {
    const results = (bulkImportResult as any)?.results|| bulkImportResult;
    if (refreshRequired(results, 'bulkImportResult')) {
      const refreshBlock = results?.some?.(
        ({version}) => !isNil(version) && version === 0
      );
      if (refreshBlock) {
        gridAPI?.refreshCurrentBlock?.();
      } else {
        gridAPI?.updateRowNodes?.(results as GenericObject[]);
      }
      postUpdate();
    }
    updateState({bulkImportResult: results});
  }, [bulkImportResult]);
};

export default useGridDataUpdate;
